import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChefHat,
  Users,
  ShieldCheck,
  Leaf,
  Clock,
  UtensilsCrossed,
  Package,
  FileText,
  Building2,
} from "lucide-react";
import { useSeo } from "@/components/Seo";

const HERO_IMAGE = "/api/images/bc70ca80-524f-44c9-9929-8f49b51ef391";

const WHY = [
  {
    icon: Users,
    title: "Built for group sizes of 50+",
    text: "Packages start at a 50-guest minimum, so the kitchen, service planning and quotations are all designed around larger gatherings from the outset.",
  },
  {
    icon: UtensilsCrossed,
    title: "Menus that scale",
    text: "Choose from 130+ vegetarian dishes — starters, mains, live counters and sweets — and the quantities are planned to your exact headcount.",
  },
  {
    icon: ShieldCheck,
    title: "Consistent quality at scale",
    text: "Food is prepared fresh in a hygienic kitchen, with the same recipes and standards whether you're serving 50 guests or several hundred.",
  },
  {
    icon: Clock,
    title: "On-time service",
    text: "Larger events run on tight schedules — service is planned around your function timings so courses go out when they should.",
  },
  {
    icon: Building2,
    title: "Experienced with big functions",
    text: "The team has served weddings, corporate events and family functions across Mumbai, where large guest counts are the norm.",
  },
  {
    icon: Leaf,
    title: "Transparent quotations",
    text: "You get a written, itemised quotation for your exact guest count to review and approve before anything is confirmed.",
  },
];

export default function CateringForLargeEventsMumbai() {
  useSeo({
    title: "Catering for Large Events & Groups in Mumbai | Tezz Khana",
    description:
      "Vegetarian catering for large events and groups in Mumbai by Tezz Khana. Flexible menus for weddings, corporate events and celebrations. Request a quote.",
    path: "/catering-for-large-events-mumbai",
    ogTitle: "Catering for Large Events & Groups in Mumbai | Tezz Khana",
    ogDescription:
      "Vegetarian catering for large events in Mumbai — flexible menus for bigger gatherings with custom quotations from Tezz Khana.",
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-large-events-catering";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Large event catering",
      name: "Catering for Large Events & Groups in Mumbai",
      description:
        "Vegetarian catering for large events and groups in Mumbai, with scalable menus, flexible packages and custom quotations.",
      url: "https://tezzkhana.com/catering-for-large-events-mumbai",
      areaServed: [
        { "@type": "City", name: "Mumbai" },
        { "@type": "City", name: "Navi Mumbai" },
        { "@type": "City", name: "Thane" },
      ],
      provider: {
        "@type": "FoodEstablishment",
        name: "Tezz Khana Catering Service",
        url: "https://tezzkhana.com",
        servesCuisine: "Indian vegetarian",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Goregaon East, Mumbai",
          addressRegion: "Maharashtra",
          addressCountry: "IN",
        },
      },
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("ld-large-events-catering")?.remove();
    };
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="container-x py-14 sm:py-20" data-testid="lem-hero">
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <span className="kbd-eyebrow">
              <Users className="h-3 w-3" /> Tezz Khana Catering Service
            </span>
            <h1 className="mt-4 font-serif-display text-4xl font-semibold leading-tight text-secondary sm:text-5xl lg:text-6xl">
              Catering for Large Events &amp; Groups in Mumbai
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              Reliable vegetarian catering designed for larger gatherings, with menus
              that scale without compromising quality.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/request-quote"
                data-testid="lem-hero-quote"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Request a Custom Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/build-your-menu"
                data-testid="lem-hero-build"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border px-6 text-sm font-semibold text-secondary hover:bg-muted"
              >
                <ChefHat className="h-4 w-4" /> Build Your Menu
              </Link>
            </div>
          </div>
          <img
            src={HERO_IMAGE}
            alt="Pav bhaji from the Tezz Khana vegetarian menu, a crowd favourite at large events"
            className="h-64 w-full rounded-3xl object-cover shadow-lg sm:h-80 lg:h-96"
            loading="eager"
          />
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="container-x pb-14" data-testid="lem-intro">
        <div className="max-w-3xl space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
          <p>
            Tezz Khana is a vegetarian catering service based in Goregaon East, Mumbai,
            and its packages are built for gatherings of 50 guests or more. That minimum
            is the starting point, not the ceiling — the same menus scale up smoothly for
            weddings, corporate functions and community celebrations with much larger
            guest counts.
          </p>
          <p>
            Every quotation is prepared for your specific headcount. Browse the{" "}
            <Link to="/menu" data-testid="lem-menu-link" className="font-semibold text-primary hover:underline">
              full menu
            </Link>
            , pick a package or build your own selection, share your guest count, and
            request a customised quotation for your event.
          </p>
        </div>
      </section>

      {/* WHY CHOOSE FOR LARGE GROUPS */}
      <section className="container-x pb-16" data-testid="lem-why">
        <span className="kbd-eyebrow">Why Tezz Khana for big gatherings</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          Why choose us for large groups
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w) => (
            <div key={w.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <w.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 text-sm font-semibold text-secondary">{w.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FLEXIBLE PACKAGES */}
      <section className="container-x pb-16" data-testid="lem-packages">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <Package className="h-5 w-5" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-secondary md:text-lg">Flexible packages</h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Start from the Silver, Gold or Platinum packages, or go fully custom. Each
            package is adjusted to your event type and guest count before you receive a
            quotation — or use the{" "}
            <Link to="/build-your-menu" data-testid="lem-build-link" className="font-semibold text-primary hover:underline">
              menu builder
            </Link>{" "}
            to hand-pick every dish for your group.
          </p>
          <Link
            to="/packages"
            data-testid="lem-packages-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            View packages <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="container-x pb-16" data-testid="lem-service-area">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="kbd-eyebrow">Service area</span>
          <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
            Serving large events across Mumbai, Navi Mumbai and Thane
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Based in Goregaon East, Tezz Khana caters large gatherings across Mumbai —
            from South Mumbai to the Western and Central suburbs — as well as Navi Mumbai
            and Thane. Share your venue location and guest count while requesting your
            quote and we&apos;ll plan the service around it.
          </p>
        </div>
      </section>

      {/* HOW TO REQUEST A QUOTE */}
      <section className="container-x pb-16" data-testid="lem-steps">
        <span className="kbd-eyebrow">How it works</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          How to request a large-event catering quote
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            { n: "1", t: "Tell us your guest count", d: "Share the occasion, date and expected number of guests (50 and up)." },
            { n: "2", t: "Select or customise your menu", d: "Pick a package or build your own dish-by-dish selection." },
            { n: "3", t: "Request a quotation", d: "Receive an itemised quote for your headcount to review, adjust and approve." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-border/70 bg-card p-6">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">{s.n}</span>
              <h3 className="mt-3 text-sm font-semibold text-secondary">{s.t}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <Link
          to="/request-quote"
          data-testid="lem-steps-quote-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
        >
          <FileText className="h-4 w-4" /> Go to the quote request form <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary py-14 text-primary-foreground sm:py-16" data-testid="lem-cta">
        <div className="container-x flex flex-col items-center justify-between gap-6 sm:flex-row">
          <div>
            <h2 className="font-serif-display text-2xl font-semibold sm:text-3xl">
              Planning a Large Event in Mumbai?
            </h2>
            <p className="mt-2 max-w-xl text-sm text-primary-foreground/80">
              Tell us your guest count and event requirements, and we&apos;ll help you
              plan the right menu.
            </p>
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <Link
              to="/request-quote"
              data-testid="lem-cta-quote"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent px-6 text-sm font-semibold text-secondary transition-transform hover:-translate-y-0.5"
            >
              Request Custom Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/build-your-menu"
              data-testid="lem-cta-build"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-primary-foreground/40 px-6 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ChefHat className="h-4 w-4" /> Build Your Menu
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
